import {
  type DemoSessionActivity,
  type GetDemoSessionBreakdownArgs,
  useInfiniteScrollFromCache,
  useLazyGetDemoSessionsBreakdownQuery,
} from '@carboncopyco/data';
import {
  TextBase,
  TextCaption,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import {
  formatPlural,
  getTimezoneName,
  secondsToHHmmss,
} from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { TimestampCol } from '../../../../Dashboard/SessionBreakdownComponents';
import type { CsvParams } from '../../../../Dashboard/SessionBreakdownComponents/types';
import type { Col } from '../../../../FixedTable';
import { useUrlState } from '../../../../utils';
import { OpportunitiesFilter } from '../../../Controls/OpportunitiesFilter';
import { PresentersFilter } from '../../../Controls/PresentersFilter';
import { SessionsBreakdownTable } from '../../../SessionsBreakdownTable';
import type { UrlState } from '../../../types';
import { ViewedScreens } from '../ViewedScreens';

const getCols = (isOverlay?: boolean): Col<DemoSessionActivity>[] => [
  {
    alignment: 'left',
    title: 'Date & time',
    width: 150,
    maxContentWidth: 130,
    render: ({ timestamp }) => <TimestampCol timestamp={timestamp} />,
  },
  {
    alignment: 'left',
    title: 'Presenter',
    width: 180,
    render: ({ presenter }) => (
      <UserBasicInfo
        user={presenter}
        textColor="text-ds-natural-600"
        truncateWidth={130}
        size="small"
        tooltip
      />
    ),
  },
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 220,
    maxContentWidth: 190,
    render: ({ forClient }) => (
      <TooltipBubble
        title={forClient}
        placement="top-start"
      >
        <div className="truncate">
          <TextBase
            label={forClient}
            color="text-ds-natural-600"
            ellipsis
          />
        </div>
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: 'Duration',
    width: 200,
    render: (presentation) => (
      <TextBase
        label={secondsToHHmmss(presentation.durationInSeconds)}
        color="text-ds-natural-600"
      />
    ),
  },
  {
    alignment: 'right',
    title: 'Viewed screens',
    width: 200,
    render: ({ sessionId, workspaceId, organizationId, screensVisited }) => (
      <ViewedScreens
        sessionId={sessionId}
        workspaceId={workspaceId}
        organizationId={organizationId}
        screensVisited={screensVisited}
        isOverlay={isOverlay}
      />
    ),
  },
];

type PresentationsSessionsBreakdownType = {
  organizationId: string;
  workspaceId: string;
  setCsvParams: (params: CsvParams) => void;
  isOverlay?: boolean;
  demoId: string;
};

export const PresentationsSessionsBreakdown = ({
  organizationId,
  workspaceId,
  demoId,
  setCsvParams,
  isOverlay,
}: PresentationsSessionsBreakdownType) => {
  const [showAll, setShowAll] = useState(false);
  const [urlState, setUrlState] = useUrlState<UrlState>({});
  const { presenters, opportunities } = urlState;
  const cols = getCols(isOverlay);

  const fetchArgs = useMemo<GetDemoSessionBreakdownArgs>(
    () => ({
      organizationId,
      shortSessions: true,
      internalSessions: true,
      workspaceIds: [workspaceId],
      timezone: getTimezoneName(),
      opportunities: opportunities ? opportunities.split(',') : undefined,
      presenters: presenters ? presenters.split(',') : undefined,
      demoId,
      withScreenshots: false,
      resourceTypes: ['managed_presentation'],
    }),
    [demoId, opportunities, organizationId, presenters, workspaceId]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<
    DemoSessionActivity,
    GetDemoSessionBreakdownArgs
  >(useLazyGetDemoSessionsBreakdownQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs, fetchFirstBatch]);

  const clearFilters = () => {
    setUrlState({
      ...urlState,
      presenters: undefined,
      opportunities: undefined,
    });
  };

  useEffect(() => {
    setCsvParams({
      params: {
        ...fetchArgs,
        resourceTypes: ['managed_presentation'],
      },
      isDisabled: (items && !items.length) || error,
    });
  }, [items, fetchArgs, error, setCsvParams]);

  return (
    <>
      <div className="w-full flex items-center justify-between px-6 py-5">
        <div className="flex gap-3">
          <PresentersFilter
            organizationId={organizationId}
            demoId={demoId}
            workspaceId={workspaceId}
          />
          <OpportunitiesFilter
            organizationId={organizationId}
            demoId={demoId}
            workspaceId={workspaceId}
          />
        </div>
        {!!items?.length && (
          <TextCaption
            color="text-ds-natural-500"
            label={`${
              totalSize > 7 && !showAll ? 7 : items.length
            } ${formatPlural('session', items.length)} (out of ${totalSize})`}
          />
        )}
      </div>
      <SessionsBreakdownTable
        showAll={showAll}
        setShowAll={setShowAll}
        totalSize={totalSize}
        onClear={clearFilters}
        error={error}
        handleFetchNextBatch={fetchNextBatch}
        cols={cols}
        data={items}
        isLoading={isLoading}
        isLoadingBatch={isLoadingBatch}
        noResults={(presenters || opportunities) && !items?.length}
      />
    </>
  );
};
